<template src="./clientChat.html"></template>

<script lang="ts">
    import { defineComponent, nextTick } from 'vue';
    import { HubConnectionState } from '@microsoft/signalr';
    import { axiosRequests } from '../../services/axiosinstance';
    import { getFormatedDate, convertToISOString, editUrl } from '../../services/helper';
    import { Routes } from '../../const';
    import { User } from '../../models/User';
    import { ChatTemplate } from '../../models/ChatTemplate';
    import ChatTemplateDropdown from './ChatTemplateDropdown.vue';

    interface MessageModel {
        clientId: number,
        sendDate: string,
        text: string,
        messageId: number,
        isRead: boolean,
        isTechnicalMessage: boolean,
        isClubMessage: boolean,
        isTranslated: boolean
    }

    interface ComponentModel {
        hubConnection: any,
        roomId: number | null,
        clubId: number | null,
        isOwner: boolean,
        currentUser: User | null,
        message: string,
        messages: MessageModel[],
        messageInformation: string,
        connectionInformation: string,
        isTextareaFocused: boolean,
        textareaHeight: number,
        textarea: any,
        isLoading: boolean,
        showStartChat: boolean,
        isTemplatesOpen: boolean,
        templates: ChatTemplate[],
        isTemplatesLoading: boolean,
        templatesLoaded: boolean,
        templatesError: boolean,
        templatesReqId: number,
        onUpdateHandler: (() => void) | null,
        onReceiveHandler: ((message: any, clientInfo: any, messageRoomId: number) => void) | null
    }

    function escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    export default defineComponent({
        name: 'ClientChat',
        components: { ChatTemplateDropdown },
        props: ['clientId', 'clientClubId'],
        emits: ['updateBadge'],
        data(): ComponentModel {
            return {
                hubConnection: null,
                roomId: null,
                clubId: null,
                isOwner: false,
                currentUser: null,
                message: "",
                messages: [],
                messageInformation: "",
                connectionInformation: "",
                isTextareaFocused: false,
                textareaHeight: 44,
                textarea: null,
                isLoading: false,
                showStartChat: false,
                isTemplatesOpen: false,
                templates: [],
                isTemplatesLoading: false,
                templatesLoaded: false,
                templatesError: false,
                templatesReqId: 0,
                onUpdateHandler: null,
                onReceiveHandler: null
            }
        },
        methods: {
            getRoomForClient: async function () {
                try {
                    this.isLoading = true;
                    this.messageInformation = 'chat.load_message';

                    let clubs = this.$store.getters.getSelectedClubId;
                    if (!clubs || clubs == "0") {
                        clubs = this.clientClubId;
                    }

                    if (!clubs) {
                        this.messageInformation = 'chat.no_chat_for_client';
                        return;
                    }

                    let roomResult = await axiosRequests.get(
                        `${process.env.VUE_APP_CHAT_URL}Chat/GetAdministrationRooms?clubs=${clubs}`
                    );

                    let clientRoom = roomResult.find((x: any) => x.ClientId == this.clientId);

                    if (!clientRoom) {
                        this.messageInformation = '';
                        this.showStartChat = true;
                        return;
                    }

                    this.showStartChat = false;

                    this.roomId = clientRoom.RoomId;
                    this.clubId = clientRoom.ClubId;
                    this.isOwner = clientRoom.OwnerId == this.currentUser?.id;

                    // войти в SignalR-группу комнаты (могла быть создана уже после нашего подключения к хабу)
                    this.hubConnection?.invoke("EnterRoom", this.roomId?.toString()).catch(() => {});

                    this.loadTemplates();

                    await this.getMessages();
                } catch (err: any) {
                    this.messageInformation = "";
                    if (err.status == 401) {
                        return;
                    } else if (err.status == 500) {
                        this.$store.commit('SET_ISSHOWINTERNALSERVERERROR', true);
                    } else if (err.status == 403) {
                        this.$router.push('/forbidden');
                    } else if (err.status == 400 && err.data) {
                        this.$store.commit('SET_ADD_ERROR', err.data);
                    } else {
                        this.$store.commit('SET_ADD_ERROR', 'error.something_went_wrong');
                    }
                } finally {
                    this.isLoading = false;
                }
            },
            createRoom: async function () {
                try {
                    this.isLoading = true;
                    this.showStartChat = false;
                    this.messageInformation = 'chat.load_message';

                    let clubId = this.$store.getters.getSelectedClubId;
                    if (!clubId || clubId == "0") {
                        clubId = this.clientClubId;
                    }

                    if (!clubId) {
                        this.$store.commit('SET_ADD_ERROR', 'chat.no_club_for_chat');
                        this.showStartChat = true;
                        this.messageInformation = '';
                        return;
                    }

                    let result = await axiosRequests.post(
                        `${process.env.VUE_APP_CHAT_URL}Chat/CreateAdministrationRoom`,
                        { clientId: this.clientId, clubId: parseInt(clubId) }
                    );

                    this.roomId = result.RoomId;
                    this.clubId = result.ClubId;

                    // войти в SignalR-группу только что созданной комнаты, иначе первое сообщение не придёт обратно
                    this.hubConnection?.invoke("EnterRoom", this.roomId?.toString()).catch(() => {});

                    this.loadTemplates();

                    await axiosRequests.post(
                        `${process.env.VUE_APP_CHAT_URL}Chat/AssignAsOwner`,
                        { roomId: this.roomId, isAdmin: true }
                    );
                    this.isOwner = true;

                    this.hubConnection.invoke("SendUpdate", this.roomId?.toString()).catch(() => {});

                    this.messageInformation = "";
                    await this.getMessages();
                } catch (err: any) {
                    this.messageInformation = "";
                    if (err.status == 401) {
                        return;
                    } else if (err.status == 500) {
                        this.$store.commit('SET_ISSHOWINTERNALSERVERERROR', true);
                    } else if (err.status == 403) {
                        this.$router.push('/forbidden');
                    } else if (err.status == 400 && err.data) {
                        this.$store.commit('SET_ADD_ERROR', err.data);
                    } else {
                        this.$store.commit('SET_ADD_ERROR', 'error.something_went_wrong');
                    }
                    this.showStartChat = true;
                } finally {
                    this.isLoading = false;
                }
            },
            getMessages: function () {
                this.messages = [];
                axiosRequests.get(`${process.env.VUE_APP_CHAT_URL}Chat/GetAdministrationMessages?roomId=${this.roomId}&clubId=${this.clubId}`)
                    .then(
                        (data: any[]) => {
                            this.messageInformation = "";

                            let unreadMessageIds: string[] = [];
                            let technicalMessageDate: Date | null = null;
                            let todayDate: Date = new Date();

                            for (let i = 0; i < data.length; i++) {
                                let messageDate = new Date(Date.UTC(data[i].DateCreateUTC.year, data[i].DateCreateUTC.month - 1, data[i].DateCreateUTC.day, data[i].DateCreateUTC.hour, data[i].DateCreateUTC.minute, 0, 0));

                                if (technicalMessageDate == null || technicalMessageDate.toDateString() != messageDate.toDateString()) {
                                    this.messages.push({
                                        clientId: 0,
                                        sendDate: todayDate.toDateString(),
                                        text: todayDate.toDateString() != messageDate.toDateString() ? messageDate.toLocaleDateString() : 'chat.today',
                                        messageId: 0,
                                        isTranslated: todayDate.toDateString() == messageDate.toDateString(),
                                        isRead: true,
                                        isTechnicalMessage: true,
                                        isClubMessage: false
                                    });

                                    technicalMessageDate = messageDate;
                                }

                                this.messages.push({
                                    clientId: data[i].ClientId,
                                    sendDate: getFormatedDate(convertToISOString(messageDate), "HH:mm"),
                                    text: escapeHtml(data[i].Message.toString()).replaceAll('\n', '<br>'),
                                    messageId: data[i].MessageId,
                                    isTranslated: false,
                                    isRead: data[i].Read,
                                    isTechnicalMessage: data[i].TechnicalMessage,
                                    isClubMessage: data[i].ClientId == 0 || data[i].ClientId == null
                                });

                                if (!data[i].Read) unreadMessageIds.push(data[i].MessageId);
                            }

                            nextTick(() => this.scroll());

                            if (this.isOwner && unreadMessageIds.length > 0) {
                                this.hubConnection.invoke("MarkAsReadAdministrator", this.roomId?.toString(), unreadMessageIds).catch(() => {});
                            }
                        }
                    )
                    .catch(
                        async (err: any) => {
                            this.messageInformation = "";
                            if (err.status == 401) {
                                return;
                            } else if (err.status == 400 && err.data) {
                                this.$store.commit('SET_ADD_ERROR', err.data);
                            } else {
                                this.$store.commit('SET_ADD_ERROR', 'error.something_went_wrong');
                            }
                        }
                    );
            },
            sendMessage: async function () {
                const trimmedMessage = this.message?.trim();
                if (!trimmedMessage || !this.roomId || !this.isOwner) return;

                if (!this.hubConnection || this.hubConnection.state !== HubConnectionState.Connected) {
                    this.$store.commit('SET_ADD_ERROR', 'chat.send_failed');
                    return;
                }

                try {
                    await this.hubConnection.invoke("SendAdministration", trimmedMessage, this.roomId.toString());
                    this.message = "";
                    nextTick(() => this.changeTextArea());
                } catch {
                    this.$store.commit('SET_ADD_ERROR', 'chat.send_failed');
                }
            },
            connectToChat: function () {
                this.hubConnection = this.$store.getters.getHubConnection;

                if (!this.hubConnection) {
                    this.connectionInformation = 'chat.conneting';
                    return;
                }

                this.connectionInformation = "";

                if (this.onUpdateHandler) this.hubConnection.off("Update", this.onUpdateHandler);
                if (this.onReceiveHandler) this.hubConnection.off("Receive", this.onReceiveHandler);

                // Сохраняем ссылки на обработчики, чтобы отписаться только от них
                this.onUpdateHandler = () => {
                    this.$emit('updateBadge');
                };
                this.onReceiveHandler = (message: any, clientInfo: any, messageRoomId: number) => this.receiveMessage(message, clientInfo, messageRoomId);

                this.hubConnection.on("Update", this.onUpdateHandler);
                this.hubConnection.on("Receive", this.onReceiveHandler);
            },
            receiveMessage: function (message: any, clientInfo: any, messageRoomId: number) {
                if (messageRoomId == this.roomId) {

                    let todayDate: Date = new Date();

                    for (let i = this.messages.length - 1; i >= 0; i--) {
                        if (!this.messages[i].isTechnicalMessage) continue;

                        if (this.messages[i].sendDate == todayDate.toDateString()) break;

                        this.messages.push({
                            clientId: 0,
                            sendDate: todayDate.toDateString(),
                            text: 'chat.today',
                            messageId: 0,
                            isTranslated: true,
                            isRead: true,
                            isTechnicalMessage: true,
                            isClubMessage: false
                        });
                        break;
                    }

                    this.messages.push({
                        clientId: clientInfo.clientId,
                        sendDate: getFormatedDate(convertToISOString(new Date()), "HH:mm"),
                        text: escapeHtml(message.message.toString()).replaceAll('\n', '<br>'),
                        messageId: message.messageId,
                        isTranslated: false,
                        isRead: true,
                        isTechnicalMessage: false,
                        isClubMessage: clientInfo.clientId == 0 || clientInfo.clientId == null
                    });

                    nextTick(() => this.scroll());

                    this.hubConnection.invoke("MarkAsReadAdministrator", this.roomId?.toString(), [message.messageId]).catch(() => {});
                } else {
                    this.$emit('updateBadge');
                }
            },
            assignAsOwner: async function () {
                await axiosRequests.post(`${process.env.VUE_APP_CHAT_URL}Chat/AssignAsOwner`, { roomId: this.roomId, isAdmin: true })
                    .then(
                        () => {
                            this.isOwner = true;
                            this.hubConnection.invoke("SendUpdate", this.roomId?.toString()).catch(() => {});

                            this.hubConnection.invoke("MarkAsReadAdministrator", this.roomId?.toString(), this.messages.filter(x => x.isRead == false).map(x => x.messageId)).catch(() => {});
                        }
                    )
                    .catch(
                        async (err: any) => {
                            if (err.status == 401) {
                                return;
                            } else if (err.status == 500) {
                                this.$store.commit('SET_ISSHOWINTERNALSERVERERROR', true);
                            } else if (err.status == 403) {
                                this.$router.push('/forbidden');
                            } else if (err.status == 400 && err.data) {
                                this.$store.commit('SET_ADD_ERROR', err.data);
                            } else {
                                this.$store.commit('SET_ADD_ERROR', 'error.something_went_wrong');
                            }
                        }
                    );
            },
            removeOwner: async function () {
                await axiosRequests.post(`${process.env.VUE_APP_CHAT_URL}Chat/RemoveOwner`, { roomId: this.roomId })
                    .then(
                        () => {
                            this.isOwner = false;
                            this.hubConnection.invoke("SendUpdate", this.roomId?.toString()).catch(() => {});
                        }
                    )
                    .catch(
                        async (err: any) => {
                            if (err.status == 401) {
                                return;
                            } else if (err.status == 500) {
                                this.$store.commit('SET_ISSHOWINTERNALSERVERERROR', true);
                            } else if (err.status == 403) {
                                this.$router.push('/forbidden');
                            } else if (err.status == 400 && err.data) {
                                this.$store.commit('SET_ADD_ERROR', err.data);
                            } else {
                                this.$store.commit('SET_ADD_ERROR', 'error.something_went_wrong');
                            }
                        }
                    );
            },
            scroll: function () {
                let element = document.getElementById("clientChatMessages");
                if (!element) return;

                element.scrollTo({
                    top: element.getBoundingClientRect().bottom + 10000000,
                    left: 0,
                    behavior: "smooth",
                });
            },
            updateTextarea: function (e: any) {
                if (e.keyCode == 13 && !e.shiftKey && !e.ctrlKey) {
                    this.sendMessage();
                    e.preventDefault();
                    return;
                }
            },
            changeTextArea: function () {
                if (!this.textarea) this.textarea = document.getElementById("clientChatTextarea");

                if (!this.textarea) return;

                this.textarea.style.height = '44px';
                if (this.textarea.scrollHeight < 44) {
                    this.textareaHeight = 44;
                    this.textarea.style.height = '44px';
                    return;
                }

                if (this.textarea.scrollHeight <= 145) {
                    this.textareaHeight = (this.textarea.scrollHeight);
                } else {
                    this.textareaHeight = 145;
                }

                this.textarea.style.height = `${this.textarea.scrollHeight}px`;
            },
            loadTemplates: async function () {
                if (!this.clubId) return;

                const reqId = ++this.templatesReqId;
                this.templates = [];
                this.templatesLoaded = false;
                this.templatesError = false;
                this.isTemplatesLoading = true;

                try {
                    const data = await axiosRequests.get(editUrl(Routes.getChatMessageTemplates) + '?clubId=' + this.clubId);
                    if (reqId !== this.templatesReqId) return; // пришёл устаревший ответ — игнорируем
                    this.templates = (data ?? []).map((t: any) => ({ id: t.id, text: t.text }));
                    this.templatesLoaded = true;
                } catch {
                    if (reqId !== this.templatesReqId) return;
                    this.templatesError = true;
                    this.templates = [];
                } finally {
                    if (reqId === this.templatesReqId) this.isTemplatesLoading = false;
                }
            },
            toggleTemplates: function () {
                this.isTemplatesOpen = !this.isTemplatesOpen;
            },
            selectTemplate: function (template: ChatTemplate) {
                this.message = template.text;
                this.isTemplatesOpen = false;
                nextTick(() => {
                    this.changeTextArea();
                    const el = document.getElementById("clientChatTextarea") as HTMLTextAreaElement | null;
                    if (el) {
                        el.focus();
                        el.selectionStart = el.selectionEnd = el.value.length;
                    }
                });
            }
        },
        watch: {
            'isChatReconnecting': function () {
                if (this.isChatReconnecting) {
                    this.connectionInformation = 'chat.conneting';
                    this.textareaHeight = 44;
                    return;
                }

                if (!this.isChatReconnecting && this.isChatConnected) {
                    this.connectToChat();

                    nextTick(() => this.changeTextArea());

                    return;
                }
            },
            'isChatConnected': function () {
                if (!this.isChatReconnecting && !this.isChatConnected) {
                    this.connectionInformation = 'chat.conneting_error';
                    this.textareaHeight = 44;
                    return;
                }

                if (!this.isChatReconnecting && this.isChatConnected) {
                    this.connectToChat();

                    nextTick(() => this.changeTextArea());

                    return;
                }
            },
            'clientId': function() {
                this.getRoomForClient();
            }
        },
        computed: {
            isChatReconnecting: function () {
                return this.$store.getters.getIsChatReconnecting;
            },
            isChatConnected: function () {
                return this.$store.getters.getIsChatConnected;
            },
            showTemplatesButton: function () {
                // прячем кнопку, только если точно знаем, что у клуба нет шаблонов
                return !(this.templatesLoaded && !this.templatesError && this.templates.length === 0);
            },
        },
        async beforeMount() {
            this.currentUser = this.$store.getters.getCurrentUser;

            await this.connectToChat();
            await this.getRoomForClient();
        },
        beforeUnmount() {
            // Отписываемся только от своих обработчиков, не трогая чужие
            if (this.onUpdateHandler) {
                this.hubConnection?.off("Update", this.onUpdateHandler);
            }
            if (this.onReceiveHandler) {
                this.hubConnection?.off("Receive", this.onReceiveHandler);
            }
        }
    });
</script>

<style scoped>
    .client_chat_wrapper {
        height: calc(100vh - 292px);
        overflow: hidden;
        display: flex;
        margin-top: 12px;
    }

    .chat_body_wrapper {
        border-radius: 12px;
        border: 1.5px solid #d5d5d5;
        overflow: hidden;
        height: 100%;
        width: 100%;
        box-sizing: border-box;
    }

    .chat_body_top_wrapper {
        height: 60px;
        background-color: #f6f7f8;
    }

    .chat_body {
        height: calc(100%);
        overflow: auto;
        position: relative;
        padding: 0px 12px;
        overflow-x: hidden;
    }

    .chat_body_top {
        display: flex;
        justify-content: flex-end;
        padding-right: 12px;
    }

    .button_exit {
        width: 190px;
        height: 36px;
        padding: 8px 12px;
        border-radius: 12px;
        background-color: #fbe0e0;
        border: none;
        cursor: pointer;
        display: flex;
        justify-content: center;
        font-size: 16px;
        margin-top: 10px;
    }

    .button_exit_img {
        margin-right: 0px;
        margin-top: 1px;
        margin-left: 5px;
        height: 16px;
    }

    .button_owner {
        width: 190px;
        height: 36px;
        padding: 8px 12px;
        border-radius: 12px;
        background-color: #21a7d1;
        border: none;
        cursor: pointer;
        display: flex;
        justify-content: center;
        font-size: 16px;
        margin-top: 10px;
        color: white;
    }

    .button_owner_img {
        margin-right: 0px;
        margin-top: 1px;
        margin-left: 5px;
        height: 16px;
    }

    .template_button {
        padding: 8px 12px;
        border-radius: 12px;
        background-color: #f6f7f8;
        border: 1.5px solid #D5D5D5;
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        height: 46px;
        width: 46px;
        margin-top: 18px;
        margin-left: 12px;
        flex-shrink: 0;
        box-sizing: border-box;
        transition: border-color 150ms ease, background-color 150ms ease;
    }

    .template_button:hover {
        border-color: #21A7D1;
    }

    .template_button_active {
        border-color: #21A7D1;
        background-color: #e8f7fb;
    }

    .template_button_img {
        height: 22px;
        width: 22px;
    }

    .message_send_button {
        padding: 8px 12px;
        border-radius: 12px;
        background-color: #21a7d1;
        border: none;
        cursor: pointer;
        display: flex;
        justify-content: center;
        height: 46px;
        width: 46px;
        margin-top: 18px;
    }

    .message_send_button_img {
        margin-right: 0px;
        margin-top: 4px;
        margin-left: 0px;
        height: 22px;
    }

    .info_text {
        font-weight: bold;
        margin-top: 0px;
        margin-bottom: 0px;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 100%;
        text-align: center;
    }

    .start_chat_wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        gap: 16px;
    }

    .info_text_static {
        font-weight: bold;
        text-align: center;
        margin: 0;
    }

    .button_start_chat {
        padding: 10px 24px;
        border-radius: 12px;
        background-color: #21a7d1;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 16px;
        color: white;
    }

    .button_start_chat_img {
        height: 16px;
        filter: brightness(0) invert(1);
    }

    .technical_message {
        border-radius: 16px;
        background-color: #f6f7f8;
        color: #65656F;
        display: flex;
        width: fit-content;
        padding: 8px 16px;
        margin: 18px auto;
    }

    .club_message {
        border-radius: 16px;
        background-color: #f6f7f8;
        color: black;
        display: flex;
        padding: 20px 16px;
        margin: 6px 12px;
        position: relative;
        width: fit-content;
        min-width: 150px;
        margin-inline: auto 0px;
    }

    .client_message {
        border-radius: 16px;
        background-color: #c7e9f3;
        color: black;
        display: flex;
        padding: 20px 16px;
        margin: 6px 0px;
        position: relative;
        width: fit-content;
        min-width: 150px;
    }

    .message_date {
        margin: 0;
        position: absolute;
        font-size: 12px;
        bottom: 5px;
        right: 16px;
    }

    .chat_content_wrapper {
        height: calc(100% - 60px);
        overflow: hidden;
        display: flex;
        flex-flow: column;
        width: 100%;
    }

    .chat_body_footer_wrapper {
        background-color: #f6f7f8;
        position: relative;
        height: fit-content;
        display: flex;
        width: 100%;
    }

    .chat_body_footer {
        display: flex;
        width: 100%;
        height: fit-content;
    }

    .textarea_wrapper {
        overflow: hidden;
        border-radius: 12px;
        border: 1.5px solid #D5D5D5;
        margin-left: 12px;
        margin-right: 12px;
        margin-top: 18px;
        margin-bottom: 18px;
        width: calc(100% - 143px) !important;
        display: flex;
    }

    /* когда кнопка шаблонов скрыта (у клуба нет шаблонов) — занимаем освободившееся место */
    .textarea_wrapper.no-template-button {
        width: calc(100% - 85px) !important;
    }

    .active {
        border: 1.5px solid #21A7D1 !important;
    }

    .textarea {
        border: unset;
        padding: 11px 12px;
        font-size: 16px;
        width: 100%;
        height: 44px;
        resize: none;
        box-sizing: border-box;
        max-height: 145px;
    }

    .textarea:focus-visible {
        outline: unset;
        border: unset;
    }

    @media(max-width: 1000px) {
        .button_exit {
            width: 45px;
        }

        .button_text {
            display: none;
        }

        .button_exit_img {
            margin-right: 7px;
            margin-top: 1px;
            margin-left: 10px;
            height: 16px;
        }

        .button_owner {
            width: 45px;
        }

        .button_owner_img {
            margin-right: 7px;
            margin-top: 1px;
            margin-left: 10px;
            height: 16px;
        }
    }

    @media(max-width: 768px) {
        .client_chat_wrapper {
            height: calc(100vh - 272px);
        }

        .button_text {
            display: none;
        }
    }

    @media(max-width: 500px) {
        .technical_message {
            font-size: 12px;
        }

        .club_message {
            padding: 16px 16px;
            font-size: 14px;
        }

        .client_message {
            padding: 16px 16px;
            font-size: 14px;
        }
    }
</style>
